import { useAutocompleteWorkOrdersQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import RedAlert from '@/gf/components/RedAlert'
import Spinner from '@/gf/components/Spinner'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/gf/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import SelectorTrigger from '@/gf/components/ui/selector-trigger'
import useDebounce from '@/gf/hooks/useDebounce'
import { cn } from '@/gf/lib/utils'
import { X } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useState } from 'react'

const ToggleFilter = ({
  enabled,
  onChange,
  children,
}: {
  enabled: boolean
  onChange: (enabled: boolean) => void
  children: ReactNode
}) => (
  <button
    className={cn(
      'flex flex-row items-center gap-x-1 px-2 py-1 text-sm font-medium rounded-full border group max-w-44',
      enabled
        ? 'text-white bg-blue-600 border-blue-700 hover:bg-blue-700 hover:border-blue-800'
        : 'text-gray-900 border-gray-300 hover:bg-gray-50'
    )}
    onClick={() => onChange(!enabled)}
  >
    <span className="truncate">{children}</span>
    {enabled && (
      <div className="-m-0.5 -mr-1 p-0.5 flex flex-shrink-0 rounded-full hover:bg-blue-900">
        <X className="text-white w-4 h-4" />
      </div>
    )}
  </button>
)

const WorkOrderCombobox = ({
  workOrder,
  onWorkOrderChange,
  unitNumber,
}: {
  workOrder: string | undefined
  onWorkOrderChange: (workOrder: string) => void
  unitNumber?: string
}) => {
  const client = useGqlClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [openFilter, setOpenFilter] = useState(true)
  const [unitFilter, setUnitFilter] = useState(!!unitNumber)
  const { data, loading, error } = useAutocompleteWorkOrdersQuery({
    client,
    variables: {
      search: debouncedSearch,
      open: openFilter || null,
      unitNumber: (unitFilter && unitNumber) || null,
    },
  })

  const workOrders = useMemo(() => {
    return data?.autocompleteWorkOrders ?? []
  }, [data])

  useEffect(() => {
    setUnitFilter(!!unitNumber)
  }, [unitNumber])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SelectorTrigger value={workOrder} placeholder="Select work order" />
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter={false}>
          <div>
            <CommandInput
              placeholder="Search work orders..."
              value={search}
              onValueChange={setSearch}
            />
            <div className="py-1 px-2 flex flex-row gap-x-1 overflow-x-scroll">
              <ToggleFilter enabled={openFilter} onChange={setOpenFilter}>
                Open
              </ToggleFilter>
              {unitNumber && (
                <ToggleFilter enabled={unitFilter} onChange={setUnitFilter}>
                  Unit {unitNumber}
                </ToggleFilter>
              )}
            </div>
          </div>
          <CommandList>
            {error ? (
              <RedAlert className="bg-transparent" title="Error loading work orders." />
            ) : loading ? (
              <CommandLoading>
                <div className="flex flex-row gap-x-2 items-center justify-center">
                  <Spinner className="mx-0" size="xs" color="blue" />
                  Searching work orders...
                </div>
              </CommandLoading>
            ) : !debouncedSearch && !openFilter && !unitFilter ? (
              <CommandEmpty>Search for a work order.</CommandEmpty>
            ) : workOrders.length === 0 ? (
              <CommandEmpty>No work orders found.</CommandEmpty>
            ) : null}
            <CommandGroup>
              {workOrders.map((workOrder) => (
                <CommandItem
                  key={workOrder.id}
                  value={workOrder.id}
                  keywords={[workOrder.id, workOrder.locationId ?? '']}
                  onSelect={() => {
                    setSearch('')
                    setOpen(false)
                    onWorkOrderChange(workOrder.id)
                  }}
                >
                  <div className="flex flex-col gap-y-0 items-start">
                    <span className="font-medium">{workOrder.id}</span>
                    {workOrder.locationId && (
                      <span className="text-gray-700 text-sm">{workOrder.locationId}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default WorkOrderCombobox
